import axios from "axios";
import { Store } from "redux/Store";
import { clearAccessToken, setAccessToken } from "@/redux/actions/auth-actions";
import CONFIG from "@/config";

const authUrl = `${CONFIG.API_BASE_URL}/auth-service/api/v1/auth`;

const axiosInstance = axios.create({
  timeout: 30000,
  headers: {
    "Client-Type": "ADMIN",
    "Client-Id": "WAYAQUICK",
  },
});

const isJsonErrorPayload = (data) =>
  Boolean(data) && typeof data === "object" && !Array.isArray(data);

// Proxies/gateways (nginx, load balancers) return HTML or plain-text bodies
// on 502/503/504 etc. Every service catch block does `error.response?.data || error`,
// which happily throws that raw string — leaving callers with no `.message` to
// read and falling back to whatever default they hardcoded (e.g. login's
// "Invalid credentials"). Normalize non-JSON bodies here so every caller gets
// a proper `{ message }` object that reflects what actually happened.
const normalizeErrorResponse = (error) => {
  if (!error?.response) return error;

  const { data, status } = error.response;

  if (!isJsonErrorPayload(data)) {
    const message =
      status >= 500
        ? "The server is temporarily unavailable. Please try again shortly."
        : status
          ? `Request failed (${status}). Please try again.`
          : "Something went wrong. Please try again.";

    error.response.data = { status: false, message };
  }

  return error;
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = Store.getState().auth.accessToken;

    if (token && !config.headers["Authorization"]) {
      config.headers["Authorization"] = token;
    } else if (!token) {
      delete config.headers["Authorization"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const prevRequest = error?.config;

    const isAuthRoute =
      prevRequest?.url?.includes("/auth/login") ||
      prevRequest?.url?.includes("/auth/register") ||
      prevRequest?.url?.includes("/auth/generate-login-otp") ||
      prevRequest?.url?.includes("/auth/password/forgot-password") ||
      prevRequest?.url?.includes("/refresh-token") || 
      prevRequest?.url?.includes("/admin/wayaquick-official/otp-send") ||
      prevRequest?.url?.includes("/admin/wayaquick-official/otp-verify");
    
    if (
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      !prevRequest?._sent &&
      !isAuthRoute
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            prevRequest.headers["Authorization"] = token;
            return axiosInstance(prevRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      prevRequest._sent = true;
      isRefreshing = true;

      try {
        const res = await axios.post(
          `${authUrl}/refresh-token`,
          {},
          {
            withCredentials: true,
            headers: {
              "Client-Type": "ADMIN",
              "Client-Id": "WAYAQUICK",
            },
          },
        );

        const newToken = res?.data?.data?.token ?? res?.data?.token;

        Store.dispatch(setAccessToken({ token: newToken }));
        processQueue(null, newToken);

        prevRequest.headers = {
          ...prevRequest.headers,
          Authorization: newToken,
        };

        return axiosInstance(prevRequest);
      } catch (err) {
        processQueue(err, null);
        Store.dispatch(clearAccessToken());
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(normalizeErrorResponse(error));
  },
);

export default axiosInstance;
